import { Group, Text, UnstyledButton } from '@mantine/core';
import type { ReactNode } from 'react';

/** Props for {@link SectionHead}. */
export interface SectionHeadProps {
  /** Section title. */
  title: string;
  /** Optional dimmed note rendered inline after the title (e.g. a date range). */
  note?: ReactNode;
  /** Optional trailing action label (e.g. "See all", "Add"). Renders only when `onAction` is also set. */
  action?: string;
  /** Handler for the trailing action. */
  onAction?: () => void;
}

/**
 * SectionHead — a section-scale heading, one size down from {@link PageHeader}.
 *
 * Shell: any. Used inside a page to introduce a card/list/table section (e.g.
 * "Recent activity", "This show's expenses") — `PageHeader` remains the
 * page-level (h1) title bar; this is deliberately a sibling, not a
 * replacement, per the ratified primitive-vocabulary mapping
 * (`docs/redesign-decision-brief.md` §2.2: "Different scale — page vs
 * section. Genuine gap at section scale. Adopt as a sibling, not a
 * replacement.").
 *
 * The action affordance is a full `--tap-min` (44px) tall tap target even
 * though it renders as small text, so it stays legally tappable on the phone
 * canvas.
 */
export function SectionHead({ title, note, action, onAction }: SectionHeadProps) {
  return (
    <Group justify="space-between" align="center" wrap="nowrap" mih="var(--tap-min)">
      <Group gap={8} align="baseline" wrap="nowrap" px={2} style={{ minWidth: 0 }}>
        <Text fw={600} fz={15} style={{ letterSpacing: '-0.01em', whiteSpace: 'nowrap' }}>
          {title}
        </Text>
        {note != null && (
          <Text fz={12} c="dimmed" truncate>
            {note}
          </Text>
        )}
      </Group>
      {action != null && onAction != null && (
        <UnstyledButton
          onClick={onAction}
          style={{
            minHeight: 'var(--tap-min)',
            display: 'grid',
            placeItems: 'center',
            padding: '0 6px',
            color: 'var(--accent-text)',
            flexShrink: 0,
          }}
        >
          <Text fz={12} fw={600} c="inherit">
            {action}
          </Text>
        </UnstyledButton>
      )}
    </Group>
  );
}
